import {produce} from 'immer';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {
  AntibodyChainType,
  AntibodyDomainType,
  AntibodyFormatDomainTerminusStyle,
} from 'graphql-client/__generated/graphqlGlobalTypes';

export const getSingleDomainModule = (domainType: AntibodyDomainType): AntibodyFormat => ({
  id: 'placeholder',
  name: 'placeholder',
  originalVeritas: null,
  chainPositions: [
    {
      ref: 2,
      domainRefs: [1],
      type: AntibodyChainType.NONE,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: null,
      ref: 1,
      type: domainType,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: domainType,
      specificity: null,
      label: domainType,
      optional: domainType === AntibodyDomainType.L,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.NONE,
      label: 'Chain',
      refs: [2],
      domainRefsPerChain: [[1]],
      domains: [
        {
          type: domainType,
          specificity: null,
          label: domainType,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [],
  interactions: [],
  visualization: {
    domainVisualizations: [
      {
        ref: 1,
        translate: {
          x: 16,
          y: 16,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 96,
        svgViewboxWidth: 64,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  archiveData: {
    __typename: 'ArchiveDataNode',
    archived: false,
    purpose: null,
  },
  builtIn: false,
  __typename: 'AntibodyFormatNode',
});

export const getTwoDomainModule = (
  domainType1: AntibodyDomainType,
  domainType2: AntibodyDomainType
): AntibodyFormat => ({
  id: 'placeholder',
  name: 'placeholder',
  originalVeritas: null,
  chainPositions: [
    {
      ref: 3,
      domainRefs: [1, 2],
      type: AntibodyChainType.NONE,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: null,
      ref: 1,
      type: domainType1,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: domainType2,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: domainType1,
      specificity: null,
      label: domainType1,
      optional: domainType1 === AntibodyDomainType.L,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: domainType2,
      specificity: null,
      label: domainType2,
      optional: domainType2 === AntibodyDomainType.L,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.NONE,
      label: 'Chain',
      refs: [3],
      domainRefsPerChain: [[1, 2]],
      domains: [
        {
          type: domainType1,
          specificity: null,
          refs: [1],
          label: domainType1,
          __typename: 'DistinctDomainNode',
        },
        {
          type: domainType2,
          specificity: null,
          refs: [2],
          label: domainType2,
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [],
  interactions: [],
  visualization: {
    domainVisualizations: [
      {
        ref: 2,
        translate: {
          x: 16,
          y: 84,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 16,
          y: 16,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 164,
        svgViewboxWidth: 64,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  archiveData: {
    __typename: 'ArchiveDataNode',
    archived: false,
    purpose: null,
  },
  builtIn: false,
  __typename: 'AntibodyFormatNode',
});

export const FAB: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  originalVeritas: null,
  chainPositions: [
    {
      ref: 5,
      domainRefs: [1, 2],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 6,
      domainRefs: [3, 4],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.CH1,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 3,
      type: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 4,
      type: AntibodyDomainType.CL,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.VH,
      specificity: null,
      label: AntibodyDomainType.VH,
      optional: false,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: null,
      label: AntibodyDomainType.CH1,
      optional: false,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: null,
      label: AntibodyDomainType.VL,
      optional: false,
      refs: [3],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: null,
      label: AntibodyDomainType.CL,
      optional: false,
      refs: [4],
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy',
      refs: [5],
      domainRefsPerChain: [[1, 2]],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: null,
          label: AntibodyDomainType.VH,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: null,
          label: AntibodyDomainType.CH1,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light',
      refs: [6],
      domainRefsPerChain: [[3, 4]],
      domains: [
        {
          type: AntibodyDomainType.VL,
          specificity: null,
          label: AntibodyDomainType.VL,
          refs: [3],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: null,
          label: AntibodyDomainType.CL,
          refs: [4],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [
    {
      __typename: 'DistinctVariablePairNode',
      label: 'VH-VL pair',
      vhVaVg: {
        type: AntibodyDomainType.VH,
        specificity: null,
        label: AntibodyDomainType.VH,
        refs: [1],
        __typename: 'DistinctDomainNode',
      },
      vlVbVd: {
        type: AntibodyDomainType.VL,
        specificity: null,
        label: AntibodyDomainType.VL,
        refs: [3],
        __typename: 'DistinctDomainNode',
      },
    },
  ],
  interactions: [
    {
      domainRefs: [1, 3],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 4],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 2,
        translate: {
          x: 32,
          y: 100,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 32,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 72,
          y: 100,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 72,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 196,
        svgViewboxWidth: 136,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  archiveData: {
    __typename: 'ArchiveDataNode',
    archived: false,
    purpose: null,
  },
  builtIn: false,
  __typename: 'AntibodyFormatNode',
};

export const SCFV: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  originalVeritas: null,
  chainPositions: [
    {
      ref: 4,
      domainRefs: [1, 2, 3],
      type: AntibodyChainType.NONE,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.L,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 3,
      type: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.VH,
      specificity: null,
      label: AntibodyDomainType.VH,
      optional: false,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.L,
      specificity: null,
      label: AntibodyDomainType.L,
      optional: true,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: null,
      label: AntibodyDomainType.VL,
      optional: false,
      refs: [3],
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.NONE,
      label: 'Chain',
      refs: [4],
      domainRefsPerChain: [[1, 2, 3]],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: null,
          label: AntibodyDomainType.VH,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.L,
          specificity: null,
          label: AntibodyDomainType.L,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VL,
          specificity: null,
          label: AntibodyDomainType.VL,
          refs: [3],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [
    {
      __typename: 'DistinctVariablePairNode',
      label: 'VH-VL pair',
      vhVaVg: {
        __typename: 'DistinctDomainNode',
        type: AntibodyDomainType.VH,
        specificity: null,
        label: AntibodyDomainType.VH,
        refs: [1],
      },
      vlVbVd: {
        type: AntibodyDomainType.VL,
        specificity: null,
        label: AntibodyDomainType.VL,
        refs: [3],
        __typename: 'DistinctDomainNode',
      },
    },
  ],
  interactions: [],
  visualization: {
    domainVisualizations: [
      {
        ref: 3,
        translate: {
          x: 72,
          y: 43.2,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 45.6,
          y: 103.2,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        centerLength: 64,
        endRadius: 11.2,
        tipLength: 4,
        __typename: 'LinkerDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 32,
          y: 43.2,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 139.2,
        svgViewboxWidth: 136,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  archiveData: {
    __typename: 'ArchiveDataNode',
    archived: false,
    purpose: null,
  },
  builtIn: false,
  __typename: 'AntibodyFormatNode',
};

export const SCFAB: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  originalVeritas: null,
  chainPositions: [
    {
      ref: 6,
      domainRefs: [1, 2, 3, 4, 5],
      type: AntibodyChainType.NONE,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.CH1,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 3,
      type: AntibodyDomainType.L,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 4,
      type: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 5,
      type: AntibodyDomainType.CL,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.VH,
      specificity: null,
      label: AntibodyDomainType.VH,
      optional: false,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: null,
      label: AntibodyDomainType.CH1,
      optional: false,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.L,
      specificity: null,
      label: AntibodyDomainType.L,
      optional: true,
      refs: [3],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: null,
      label: AntibodyDomainType.VL,
      optional: false,
      refs: [4],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: null,
      label: AntibodyDomainType.CL,
      optional: false,
      refs: [5],
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.NONE,
      label: 'Chain',
      refs: [6],
      domainRefsPerChain: [[1, 2, 3, 4, 5]],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: null,
          label: AntibodyDomainType.VH,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: null,
          label: AntibodyDomainType.CH1,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.L,
          specificity: null,
          label: AntibodyDomainType.L,
          refs: [3],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VL,
          specificity: null,
          label: AntibodyDomainType.VL,
          refs: [4],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: null,
          label: AntibodyDomainType.CL,
          refs: [5],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [
    {
      __typename: 'DistinctVariablePairNode',
      label: 'VH-VL pair',
      vhVaVg: {
        type: AntibodyDomainType.VH,
        specificity: null,
        label: AntibodyDomainType.VH,
        refs: [1],
        __typename: 'DistinctDomainNode',
      },
      vlVbVd: {
        type: AntibodyDomainType.VL,
        specificity: null,
        label: AntibodyDomainType.VL,
        refs: [4],
        __typename: 'DistinctDomainNode',
      },
    },
  ],
  interactions: [],
  visualization: {
    domainVisualizations: [
      {
        ref: 5,
        translate: {
          x: 72,
          y: 111.2,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 72,
          y: 43.2,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 45.6,
          y: 175.2,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        centerLength: 132,
        endRadius: 11.2,
        tipLength: 4,
        __typename: 'LinkerDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 32,
          y: 111.2,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 32,
          y: 43.2,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 207.2,
        svgViewboxWidth: 136,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  archiveData: {
    __typename: 'ArchiveDataNode',
    archived: false,
    purpose: null,
  },
  builtIn: false,
  __typename: 'AntibodyFormatNode',
};

export const FUSION: AntibodyFormat = produce(getSingleDomainModule(AntibodyDomainType.F), (draft) => {
  draft.visualization.domainVisualizations = [
    {
      ref: 1,
      translate: {
        x: 16,
        y: 16,
        __typename: 'AntibodyFormatDomainTranslationNode',
      },
      rotate: 0,
      reflectX: false,
      reflectY: false,
      __typename: 'FusionDomainVisualizationNode',
    },
  ];
  return draft;
});

export const IGG: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  builtIn: false,
  originalVeritas: null,
  archiveData: {
    archived: false,
    purpose: null,
    __typename: 'ArchiveDataNode',
  },
  chainPositions: [
    {
      ref: 19,
      domainRefs: [1, 2, 3, 4, 5, 6],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 20,
      domainRefs: [10, 11, 12, 13, 14, 15],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 9,
      domainRefs: [7, 8],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 18,
      domainRefs: [16, 17],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy)',
      ref: 2,
      type: AntibodyDomainType.CH1,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'A(Heavy)',
      ref: 3,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy)',
      ref: 4,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy)',
      ref: 5,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy)',
      ref: 6,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 10,
      type: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy)',
      ref: 11,
      type: AntibodyDomainType.CH1,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'A(Heavy)',
      ref: 12,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy)',
      ref: 13,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy)',
      ref: 14,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy)',
      ref: 15,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 7,
      type: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Light)',
      ref: 8,
      type: AntibodyDomainType.CL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 16,
      type: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Light)',
      ref: 17,
      type: AntibodyDomainType.CL,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.VH,
      specificity: null,
      label: AntibodyDomainType.VH,
      refs: [1, 10],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: '(Heavy)',
      label: `${AntibodyDomainType.CH1}(Heavy)`,
      refs: [2, 11],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'A(Heavy)',
      label: 'Mid-chain element 1 (Heavy)',
      refs: [3, 12],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: '(Heavy)',
      label: `${AntibodyDomainType.H}(Heavy)`,
      refs: [4, 13],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: '(Heavy)',
      label: `${AntibodyDomainType.CH2}(Heavy)`,
      refs: [5, 14],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: '(Heavy)',
      label: `${AntibodyDomainType.CH3}(Heavy)`,
      refs: [6, 15],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: null,
      label: AntibodyDomainType.VL,
      refs: [7, 16],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: '(Light)',
      label: `${AntibodyDomainType.CL}(Light)`,
      refs: [8, 17],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy',
      refs: [19, 20],
      domainRefsPerChain: [
        [1, 2, 3, 4, 5, 6],
        [10, 11, 12, 13, 14, 15],
      ],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: null,
          refs: [1, 10],
          label: AntibodyDomainType.VH,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: '(Heavy)',
          refs: [2, 11],
          label: `${AntibodyDomainType.CH1}(Heavy)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'A(Heavy)',
          refs: [3, 12],
          label: 'Mid-chain element 1 (Heavy)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: '(Heavy)',
          refs: [4, 13],
          label: `${AntibodyDomainType.H}(Heavy)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: '(Heavy)',
          refs: [5, 14],
          label: `${AntibodyDomainType.CH2}(Heavy)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: '(Heavy)',
          refs: [6, 15],
          label: `${AntibodyDomainType.CH3}(Heavy)`,
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light',
      refs: [9, 18],
      domainRefsPerChain: [
        [7, 8],
        [16, 17],
      ],
      domains: [
        {
          type: AntibodyDomainType.VL,
          specificity: null,
          refs: [7, 16],
          label: AntibodyDomainType.VL,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: '(Light)',
          refs: [8, 17],
          label: `${AntibodyDomainType.CL}(Light)`,
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [
    {
      label: 'VH-VL pair',
      vhVaVg: {
        type: AntibodyDomainType.VH,
        specificity: null,
        label: AntibodyDomainType.VH,
        refs: [1, 10],
        __typename: 'DistinctDomainNode',
      },
      vlVbVd: {
        type: AntibodyDomainType.VL,
        specificity: null,
        label: AntibodyDomainType.VL,
        refs: [7, 16],
        __typename: 'DistinctDomainNode',
      },
      __typename: 'DistinctVariablePairNode',
    },
  ],
  interactions: [
    {
      domainRefs: [1, 7],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 8],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [10, 16],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [11, 17],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [4, 13],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [5, 14],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [6, 15],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 6,
        translate: {
          x: 136.94802540452307,
          y: 236.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 5,
        translate: {
          x: 136.94802540452307,
          y: 168.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 150.3768749657769,
          y: 149.02684435961459,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        angle: 140.0,
        freeEndLength: 4.0,
        fixedEndLength: 24.0,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 144.9750592988885,
          y: 136.58921113206867,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 12.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 106.3513351834438,
          y: 84.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 62.64177772475912,
          y: 32.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 15,
        translate: {
          x: 176.94802540452307,
          y: 236.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 14,
        translate: {
          x: 176.94802540452307,
          y: 168.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 13,
        translate: {
          x: 192.94802540452307,
          y: 149.02684435961459,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: true,
        reflectY: false,
        angle: 140.0,
        freeEndLength: 4.0,
        fixedEndLength: 24.0,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 12,
        translate: {
          x: 200.92099151015762,
          y: 136.58921113206867,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 12.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 11,
        translate: {
          x: 207.54471562560235,
          y: 84.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 10,
        translate: {
          x: 251.254273084287,
          y: 32.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 7,
        translate: {
          x: 32.0,
          y: 57.71150438746156,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 8,
        translate: {
          x: 75.70955745868469,
          y: 109.80252651955207,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 16,
        translate: {
          x: 281.89605080904613,
          y: 57.71150438746156,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 17,
        translate: {
          x: 238.18649335036145,
          y: 109.80252651955207,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4.0,
        height: 64.0,
        width: 32.0,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6.0,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 332.09102213209053,
        svgViewboxWidth: 345.89605080904613,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const HETERO_IGG: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  builtIn: false,
  originalVeritas: null,
  archiveData: {
    archived: false,
    purpose: null,
    __typename: 'ArchiveDataNode',
  },
  chainPositions: [
    {
      ref: 19,
      domainRefs: [1, 2, 3, 4, 5, 6],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 20,
      domainRefs: [10, 11, 12, 13, 14, 15],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 9,
      domainRefs: [7, 8],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 18,
      domainRefs: [16, 17],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: 'a',
      ref: 1,
      type: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 1)',
      ref: 2,
      type: AntibodyDomainType.CH1,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'A(Heavy 1)',
      ref: 3,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 1)',
      ref: 4,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 1)',
      ref: 5,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 1)',
      ref: 6,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 10,
      type: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 2)',
      ref: 11,
      type: AntibodyDomainType.CH1,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'A(Heavy 2)',
      ref: 12,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 2)',
      ref: 13,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 2)',
      ref: 14,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 2)',
      ref: 15,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 7,
      type: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Light 1)',
      ref: 8,
      type: AntibodyDomainType.CL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 16,
      type: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Light 2)',
      ref: 17,
      type: AntibodyDomainType.CL,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.VH,
      specificity: 'a',
      label: `${AntibodyDomainType.VH}a`,
      refs: [1],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: '(Heavy 1)',
      label: `${AntibodyDomainType.CH1}(Heavy 1)`,
      refs: [2],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'A(Heavy 1)',
      label: 'Mid-chain element 1 (Heavy 1)',
      refs: [3],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: '(Heavy 1)',
      label: `${AntibodyDomainType.H}(Heavy 1)`,
      refs: [4],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: '(Heavy 1)',
      label: `${AntibodyDomainType.CH2}(Heavy 1)`,
      refs: [5],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: '(Heavy 1)',
      label: `${AntibodyDomainType.CH3}(Heavy 1)`,
      refs: [6],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VH,
      specificity: 'b',
      label: `${AntibodyDomainType.VH}b`,
      refs: [10],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: '(Heavy 2)',
      label: `${AntibodyDomainType.CH1}(Heavy 2)`,
      refs: [11],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'A(Heavy 2)',
      label: 'Mid-chain element 1 (Heavy 2)',
      refs: [12],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: '(Heavy 2)',
      label: `${AntibodyDomainType.H}(Heavy 2)`,
      refs: [13],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: '(Heavy 2)',
      label: `${AntibodyDomainType.CH2}(Heavy 2)`,
      refs: [14],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: '(Heavy 2)',
      label: `${AntibodyDomainType.CH3}(Heavy 2)`,
      refs: [15],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'a',
      label: `${AntibodyDomainType.VL}a`,
      refs: [7],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: '(Light 1)',
      label: `${AntibodyDomainType.CL}(Light 1)`,
      refs: [8],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'b',
      label: `${AntibodyDomainType.VL}b`,
      refs: [16],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: '(Light 2)',
      label: `${AntibodyDomainType.CL}(Light 2)`,
      refs: [17],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy 1',
      refs: [19],
      domainRefsPerChain: [[1, 2, 3, 4, 5, 6]],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: 'a',
          refs: [1],
          label: `${AntibodyDomainType.VH}a`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: '(Heavy 1)',
          refs: [2],
          label: `${AntibodyDomainType.CH1}(Heavy 1)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'A(Heavy 1)',
          refs: [3],
          label: 'Mid-chain element 1 (Heavy 1)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: '(Heavy 1)',
          refs: [4],
          label: `${AntibodyDomainType.H}(Heavy 1)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: '(Heavy 1)',
          refs: [5],
          label: `${AntibodyDomainType.CH2}(Heavy 1)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: '(Heavy 1)',
          refs: [6],
          label: `${AntibodyDomainType.CH3}(Heavy 1)`,
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy 2',
      refs: [20],
      domainRefsPerChain: [[10, 11, 12, 13, 14, 15]],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: 'b',
          refs: [10],
          label: `${AntibodyDomainType.VH}b`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: '(Heavy 2)',
          refs: [11],
          label: `${AntibodyDomainType.CH1}(Heavy 2)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'A(Heavy 2)',
          refs: [12],
          label: 'Mid-chain element 1 (Heavy 2)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: '(Heavy 2)',
          refs: [13],
          label: `${AntibodyDomainType.H}(Heavy 2)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: '(Heavy 2)',
          refs: [14],
          label: `${AntibodyDomainType.CH2}(Heavy 2)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: '(Heavy 2)',
          refs: [15],
          label: `${AntibodyDomainType.CH3}(Heavy 2)`,
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light 1',
      refs: [9],
      domainRefsPerChain: [[7, 8]],
      domains: [
        {
          type: AntibodyDomainType.VL,
          specificity: 'a',
          refs: [7],
          label: `${AntibodyDomainType.VL}a`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: '(Light 1)',
          refs: [8],
          label: `${AntibodyDomainType.CL}(Light 1)`,
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light 2',
      refs: [18],
      domainRefsPerChain: [[16, 17]],
      domains: [
        {
          type: AntibodyDomainType.VL,
          specificity: 'b',
          refs: [16],
          label: `${AntibodyDomainType.VL}b`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: '(Light 2)',
          refs: [17],
          label: `${AntibodyDomainType.CL}(Light 2)`,
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [
    {
      label: 'VHa-VLa pair',
      vhVaVg: {
        type: AntibodyDomainType.VH,
        specificity: 'a',
        label: `${AntibodyDomainType.VH}a`,
        refs: [1],
        __typename: 'DistinctDomainNode',
      },
      vlVbVd: {
        type: AntibodyDomainType.VL,
        specificity: 'a',
        label: `${AntibodyDomainType.VL}a`,
        refs: [7],
        __typename: 'DistinctDomainNode',
      },
      __typename: 'DistinctVariablePairNode',
    },
    {
      label: 'VHb-VLb pair',
      vhVaVg: {
        type: AntibodyDomainType.VH,
        specificity: 'b',
        label: `${AntibodyDomainType.VH}b`,
        refs: [10],
        __typename: 'DistinctDomainNode',
      },
      vlVbVd: {
        type: AntibodyDomainType.VL,
        specificity: 'b',
        label: `${AntibodyDomainType.VL}b`,
        refs: [16],
        __typename: 'DistinctDomainNode',
      },
      __typename: 'DistinctVariablePairNode',
    },
  ],
  interactions: [
    {
      domainRefs: [1, 7],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 8],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [10, 16],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [11, 17],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [4, 13],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [5, 14],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [6, 15],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 6,
        translate: {
          x: 136.94802540452307,
          y: 236.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 5,
        translate: {
          x: 136.94802540452307,
          y: 168.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 150.3768749657769,
          y: 149.02684435961459,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        angle: 140.0,
        freeEndLength: 4.0,
        fixedEndLength: 24.0,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 144.9750592988885,
          y: 136.58921113206867,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 12.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 106.3513351834438,
          y: 84.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 62.64177772475912,
          y: 32.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 15,
        translate: {
          x: 176.94802540452307,
          y: 236.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 14,
        translate: {
          x: 176.94802540452307,
          y: 168.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 13,
        translate: {
          x: 192.94802540452307,
          y: 149.02684435961459,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: true,
        reflectY: false,
        angle: 140.0,
        freeEndLength: 4.0,
        fixedEndLength: 24.0,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 12,
        translate: {
          x: 200.92099151015762,
          y: 136.58921113206867,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 12.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 11,
        translate: {
          x: 207.54471562560235,
          y: 84.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 10,
        translate: {
          x: 251.254273084287,
          y: 32.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 7,
        translate: {
          x: 32.0,
          y: 57.71150438746156,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 8,
        translate: {
          x: 75.70955745868469,
          y: 109.80252651955207,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 16,
        translate: {
          x: 281.89605080904613,
          y: 57.71150438746156,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 17,
        translate: {
          x: 238.18649335036145,
          y: 109.80252651955207,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4.0,
        height: 64.0,
        width: 32.0,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6.0,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 332.09102213209053,
        svgViewboxWidth: 345.89605080904613,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const FC: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  originalVeritas: null,
  chainPositions: [
    {
      ref: 7,
      domainRefs: [1, 2, 3],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 8,
      domainRefs: [4, 5, 6],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 3,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 4,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 5,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 6,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.H,
      specificity: null,
      label: AntibodyDomainType.H,
      optional: false,
      refs: [1, 4],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: null,
      label: AntibodyDomainType.CH2,
      optional: false,
      refs: [2, 5],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: null,
      label: AntibodyDomainType.CH3,
      optional: false,
      refs: [3, 6],
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light',
      refs: [7, 8],
      domainRefsPerChain: [
        [1, 2, 3],
        [4, 5, 6],
      ],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: null,
          label: AntibodyDomainType.H,
          refs: [1, 4],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: null,
          label: AntibodyDomainType.CH2,
          refs: [2, 5],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: null,
          label: AntibodyDomainType.CH3,
          refs: [3, 6],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [],
  interactions: [
    {
      domainRefs: [1, 4],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 5],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [3, 6],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 3,
        translate: {
          x: 32,
          y: 125.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 32,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 44.14327434188076,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 6,
        translate: {
          x: 72,
          y: 125.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 5,
        translate: {
          x: 72,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 84.14327434188075,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: true,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 221.19253331742772,
        svgViewboxWidth: 148.14327434188075,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  archiveData: {
    __typename: 'ArchiveDataNode',
    archived: false,
    purpose: null,
  },
  builtIn: false,
  __typename: 'AntibodyFormatNode',
};

export const HETERO_FC: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  originalVeritas: null,
  chainPositions: [
    {
      ref: 7,
      domainRefs: [1, 2, 3],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 8,
      domainRefs: [4, 5, 6],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: 'a',
      ref: 1,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 2,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 3,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 4,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 5,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 6,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.H,
      specificity: 'a',
      label: `${AntibodyDomainType.H}a`,
      optional: false,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: 'a',
      label: `${AntibodyDomainType.CH2}a`,
      optional: false,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'a',
      label: `${AntibodyDomainType.CH3}a`,
      optional: false,
      refs: [3],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: 'b',
      label: `${AntibodyDomainType.H}b`,
      optional: false,
      refs: [4],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: 'b',
      label: `${AntibodyDomainType.CH2}b`,
      optional: false,
      refs: [5],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'b',
      label: `${AntibodyDomainType.CH3}b`,
      optional: false,
      refs: [6],
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light 1',
      refs: [7],
      domainRefsPerChain: [[1, 2, 3]],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: 'a',
          label: `${AntibodyDomainType.H}a`,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: 'a',
          label: `${AntibodyDomainType.CH2}a`,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'a',
          label: `${AntibodyDomainType.CH3}a`,
          refs: [3],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light 2',
      refs: [8],
      domainRefsPerChain: [[4, 5, 6]],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: 'b',
          label: `${AntibodyDomainType.H}b`,
          refs: [4],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: 'b',
          label: `${AntibodyDomainType.CH2}b`,
          refs: [5],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'b',
          label: `${AntibodyDomainType.CH3}b`,
          refs: [6],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [],
  interactions: [
    {
      domainRefs: [1, 4],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 5],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [3, 6],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 3,
        translate: {
          x: 32,
          y: 125.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 32,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 44.14327434188076,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 6,
        translate: {
          x: 72,
          y: 125.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 5,
        translate: {
          x: 72,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 84.14327434188075,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: true,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 221.19253331742772,
        svgViewboxWidth: 148.14327434188075,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  archiveData: {
    __typename: 'ArchiveDataNode',
    archived: false,
    purpose: null,
  },
  builtIn: false,
  __typename: 'AntibodyFormatNode',
};

export const SC_FC: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  builtIn: false,
  originalVeritas: null,
  archiveData: {
    archived: false,
    purpose: null,
    __typename: 'ArchiveDataNode',
  },
  chainPositions: [
    {
      ref: 10,
      domainRefs: [1, 2, 3, 4, 5, 6, 7, 8, 9],
      type: AntibodyChainType.NONE,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: 'A(Chain)',
      ref: 1,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'A(Chain)',
      ref: 2,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'A(Chain)',
      ref: 3,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'B(Chain)',
      ref: 4,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Chain)',
      ref: 5,
      type: AntibodyDomainType.L,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'C(Chain)',
      ref: 6,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'B(Chain)',
      ref: 7,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'B(Chain)',
      ref: 8,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'B(Chain)',
      ref: 9,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.H,
      specificity: 'A(Chain)',
      label: `${AntibodyDomainType.H}A(Chain)`,
      refs: [1],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: 'A(Chain)',
      label: `${AntibodyDomainType.CH2}A(Chain)`,
      refs: [2],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'A(Chain)',
      label: `${AntibodyDomainType.CH3}A(Chain)`,
      refs: [3],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'B(Chain)',
      label: 'Mid-chain element 1 (Chain)',
      refs: [4],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.L,
      specificity: '(Chain)',
      label: `${AntibodyDomainType.L}(Chain)`,
      refs: [5],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'C(Chain)',
      label: 'Mid-chain element 2 (Chain)',
      refs: [6],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: 'B(Chain)',
      label: `${AntibodyDomainType.H}B(Chain)`,
      refs: [7],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: 'B(Chain)',
      label: `${AntibodyDomainType.CH2}B(Chain)`,
      refs: [8],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'B(Chain)',
      label: `${AntibodyDomainType.CH3}B(Chain)`,
      refs: [9],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.NONE,
      label: 'Chain',
      refs: [10],
      domainRefsPerChain: [[1, 2, 3, 4, 5, 6, 7, 8, 9]],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: 'A(Chain)',
          refs: [1],
          label: `${AntibodyDomainType.H}A(Chain)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: 'A(Chain)',
          refs: [2],
          label: `${AntibodyDomainType.CH2}A(Chain)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'A(Chain)',
          refs: [3],
          label: `${AntibodyDomainType.CH3}A(Chain)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'B(Chain)',
          refs: [4],
          label: 'Mid-chain element 1 (Chain)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.L,
          specificity: '(Chain)',
          refs: [5],
          label: `${AntibodyDomainType.L}(Chain)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'C(Chain)',
          refs: [6],
          label: 'Mid-chain element 2 (Chain)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: 'B(Chain)',
          refs: [7],
          label: `${AntibodyDomainType.H}B(Chain)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: 'B(Chain)',
          refs: [8],
          label: `${AntibodyDomainType.CH2}B(Chain)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'B(Chain)',
          refs: [9],
          label: `${AntibodyDomainType.CH3}B(Chain)`,
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [],
  interactions: [
    {
      domainRefs: [1, 7],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 8],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [3, 9],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 9,
        translate: {
          x: 72.0,
          y: 145.29807766203572,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 8,
        translate: {
          x: 72.0,
          y: 77.29807766203572,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 7,
        translate: {
          x: 88.0,
          y: 58.23389988955981,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: true,
        reflectY: false,
        angle: 140.0,
        freeEndLength: 4.0,
        fixedEndLength: 24.0,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 6,
        translate: {
          x: 95.97296610563455,
          y: 45.79626666201389,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 12.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 5,
        translate: {
          x: 45.59999999995378,
          y: 223.29807766200003,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        pathParameters: [
          {
            dx: 0.0,
            dy: 4.0,
            __typename: 'LinePathParameterNode',
          },
          {
            rx: 11.2,
            ry: 11.2,
            dx: 22.4,
            dy: 0.0,
            rotation: 0.0,
            direction: 'COUNTER_CLOCKWISE',
            arcSize: 'LARGE',
            __typename: 'ArcPathParameterNode',
          },
          {
            dx: 0.0,
            dy: -184.098077662,
            __typename: 'LinePathParameterNode',
          },
          {
            rx: 11.2,
            ry: 11.2,
            dx: 11.2,
            dy: -11.2,
            rotation: 0.0,
            direction: 'CLOCKWISE',
            arcSize: 'SMALL',
            __typename: 'ArcPathParameterNode',
          },
          {
            rx: 35.271,
            ry: 12.17,
            dx: 22.5580546,
            dy: 12.9018666695,
            rotation: -7.317,
            direction: 'CLOCKWISE',
            arcSize: 'SMALL',
            __typename: 'ArcPathParameterNode',
          },
        ],
        __typename: 'PathLinkerDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 45.6,
          y: 199.29807766203572,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 32.0,
          y: 145.29807766203572,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 32.0,
          y: 77.29807766203572,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 45.42884956125384,
          y: 58.23389988955981,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        angle: 140.0,
        freeEndLength: 4.0,
        fixedEndLength: 24.0,
        __typename: 'HingeDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4.0,
        height: 64.0,
        width: 32.0,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6.0,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 270.498077662,
        svgViewboxWidth: 152.0,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const MONO_FC: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  originalVeritas: null,
  chainPositions: [
    {
      ref: 4,
      domainRefs: [1, 2, 3],
      type: AntibodyChainType.NONE,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 3,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.H,
      specificity: null,
      label: AntibodyDomainType.H,
      optional: false,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: null,
      label: AntibodyDomainType.CH2,
      optional: false,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: null,
      label: AntibodyDomainType.CH3,
      optional: false,
      refs: [3],
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.NONE,
      label: 'Chain',
      refs: [4],
      domainRefsPerChain: [[1, 2, 3]],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: null,
          label: AntibodyDomainType.H,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: null,
          label: AntibodyDomainType.CH2,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: null,
          label: AntibodyDomainType.CH3,
          refs: [3],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [],
  interactions: [],
  visualization: {
    domainVisualizations: [
      {
        ref: 3,
        translate: {
          x: 32,
          y: 125.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 32,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 44.14327434188076,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 221.19253331742772,
        svgViewboxWidth: 108.14327434188075,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  archiveData: {
    __typename: 'ArchiveDataNode',
    archived: false,
    purpose: null,
  },
  builtIn: false,
  __typename: 'AntibodyFormatNode',
};

export const CH3: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  originalVeritas: null,
  chainPositions: [
    {
      ref: 5,
      domainRefs: [1, 2],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 6,
      domainRefs: [3, 4],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 3,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 4,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.H,
      specificity: null,
      label: AntibodyDomainType.H,
      optional: false,
      refs: [1, 3],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: null,
      label: AntibodyDomainType.CH3,
      optional: false,
      refs: [2, 4],
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light',
      refs: [5, 6],
      domainRefsPerChain: [
        [1, 2],
        [3, 4],
      ],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: null,
          label: AntibodyDomainType.H,
          refs: [1, 3],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: null,
          label: AntibodyDomainType.CH3,
          refs: [2, 4],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [],
  interactions: [
    {
      domainRefs: [1, 3],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 4],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 2,
        translate: {
          x: 32,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 44.14327434188076,
          y: 31.999999999999996,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 72,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 84.14327434188075,
          y: 31.999999999999996,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: true,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 153.19253331742772,
        svgViewboxWidth: 148.14327434188075,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  archiveData: {
    __typename: 'ArchiveDataNode',
    archived: false,
    purpose: null,
  },
  builtIn: false,
  __typename: 'AntibodyFormatNode',
};

export const HINGE: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  originalVeritas: null,
  chainPositions: [
    {
      ref: 3,
      domainRefs: [1],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 4,
      domainRefs: [2],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.H,
      specificity: null,
      label: AntibodyDomainType.H,
      optional: false,
      refs: [1, 2],
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light',
      refs: [3, 4],
      domainRefsPerChain: [[1], [2]],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: null,
          label: AntibodyDomainType.H,
          refs: [1, 2],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [],
  interactions: [
    {
      domainRefs: [1, 2],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 1,
        translate: {
          x: 32,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 72,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: true,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 96,
        svgViewboxWidth: 112,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  archiveData: {
    __typename: 'ArchiveDataNode',
    archived: false,
    purpose: null,
  },
  builtIn: false,
  __typename: 'AntibodyFormatNode',
};

export const MONO_HINGE: AntibodyFormat = {
  id: 'abfmt_preview',
  name: 'monoHinge',
  builtIn: false,
  originalVeritas: 'monoHinge',
  archiveData: {
    archived: false,
    purpose: null,
    __typename: 'ArchiveDataNode',
  },
  chainPositions: [
    {
      ref: 2,
      domainRefs: [1],
      type: AntibodyChainType.NONE,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: '(Chain)',
      ref: 1,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.H,
      specificity: '(Chain)',
      label: 'H(Chain)',
      refs: [1],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.NONE,
      label: 'Chain',
      refs: [2],
      domainRefsPerChain: [[1]],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: '(Chain)',
          refs: [1],
          label: 'H(Chain)',
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [],
  interactions: [],
  visualization: {
    domainVisualizations: [
      {
        ref: 1,
        translate: {
          x: 32.0,
          y: 32.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        angle: 140.0,
        freeEndLength: 16.0,
        fixedEndLength: 24.0,
        __typename: 'HingeDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4.0,
        height: 64.0,
        width: 32.0,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6.0,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 128.0,
        svgViewboxWidth: 96.0,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const MONO_CH3: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  originalVeritas: null,
  chainPositions: [
    {
      ref: 3,
      domainRefs: [1, 2],
      type: AntibodyChainType.NONE,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.H,
      specificity: null,
      label: AntibodyDomainType.H,
      optional: false,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: null,
      label: AntibodyDomainType.CH3,
      optional: false,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.NONE,
      label: 'Chain',
      refs: [3],
      domainRefsPerChain: [[1, 2]],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: null,
          label: AntibodyDomainType.H,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: null,
          label: AntibodyDomainType.CH3,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [],
  interactions: [],
  visualization: {
    domainVisualizations: [
      {
        ref: 2,
        translate: {
          x: 32,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 44.14327434188076,
          y: 31.999999999999996,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 153.19253331742772,
        svgViewboxWidth: 108.14327434188075,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  archiveData: {
    __typename: 'ArchiveDataNode',
    archived: false,
    purpose: null,
  },
  builtIn: false,
  __typename: 'AntibodyFormatNode',
};

export const HETERO_CH3: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  originalVeritas: null,
  chainPositions: [
    {
      ref: 5,
      domainRefs: [1, 2],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 6,
      domainRefs: [3, 4],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: 'a',
      ref: 1,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 2,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 3,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 4,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.H,
      specificity: 'a',
      label: `${AntibodyDomainType.H}a`,
      optional: false,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'a',
      label: `${AntibodyDomainType.CH3}a`,
      optional: false,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: 'b',
      label: `${AntibodyDomainType.H}b`,
      optional: false,
      refs: [3],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'b',
      label: `${AntibodyDomainType.CH3}b`,
      optional: false,
      refs: [4],
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light 1',
      refs: [5],
      domainRefsPerChain: [[1, 2]],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: 'a',
          label: `${AntibodyDomainType.H}a`,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'a',
          label: `${AntibodyDomainType.CH3}a`,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light 2',
      refs: [6],
      domainRefsPerChain: [[3, 4]],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: 'b',
          label: `${AntibodyDomainType.H}b`,
          refs: [3],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'b',
          label: `${AntibodyDomainType.CH3}b`,
          refs: [4],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [],
  interactions: [
    {
      domainRefs: [1, 3],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 4],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 2,
        translate: {
          x: 32,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 44.14327434188076,
          y: 31.999999999999996,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 72,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 84.14327434188075,
          y: 31.999999999999996,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: true,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 153.19253331742772,
        svgViewboxWidth: 148.14327434188075,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  archiveData: {
    __typename: 'ArchiveDataNode',
    archived: false,
    purpose: null,
  },
  builtIn: false,
  __typename: 'AntibodyFormatNode',
};

export const FAB_SCFV_HETERO_FC: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  builtIn: false,
  originalVeritas: null,
  archiveData: {
    archived: false,
    purpose: null,
    __typename: 'ArchiveDataNode',
  },
  chainPositions: [
    {
      ref: 17,
      domainRefs: [19, 1, 2, 15, 9, 10, 11, 20],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 18,
      domainRefs: [21, 6, 7, 8, 16, 12, 13, 14, 22],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 5,
      domainRefs: [23, 3, 4, 24],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: 'A(Heavy 1)',
      ref: 19,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 1,
      type: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 1)',
      ref: 2,
      type: AntibodyDomainType.CH1,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'B(Heavy 1)',
      ref: 15,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 1)',
      ref: 9,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 1)',
      ref: 10,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 1)',
      ref: 11,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'C(Heavy 1)',
      ref: 20,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'A(Heavy 2)',
      ref: 21,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 6,
      type: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 2)',
      ref: 7,
      type: AntibodyDomainType.L,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 8,
      type: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'B(Heavy 2)',
      ref: 16,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 2)',
      ref: 12,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 2)',
      ref: 13,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 2)',
      ref: 14,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'C(Heavy 2)',
      ref: 22,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'A(Light)',
      ref: 23,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 3,
      type: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Light)',
      ref: 4,
      type: AntibodyDomainType.CL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'B(Light)',
      ref: 24,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'A(Heavy 1)',
      label: 'N-terminal element (Heavy 1)',
      refs: [19],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VH,
      specificity: 'a',
      label: `${AntibodyDomainType.VH}a`,
      refs: [1],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: '(Heavy 1)',
      label: `${AntibodyDomainType.CH1}(Heavy 1)`,
      refs: [2],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'B(Heavy 1)',
      label: 'Mid-chain element 1 (Heavy 1)',
      refs: [15],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: '(Heavy 1)',
      label: `${AntibodyDomainType.H}(Heavy 1)`,
      refs: [9],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: '(Heavy 1)',
      label: `${AntibodyDomainType.CH2}(Heavy 1)`,
      refs: [10],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: '(Heavy 1)',
      label: `${AntibodyDomainType.CH3}(Heavy 1)`,
      refs: [11],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'C(Heavy 1)',
      label: 'C-terminal element (Heavy 1)',
      refs: [20],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'A(Heavy 2)',
      label: 'N-terminal element (Heavy 2)',
      refs: [21],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VH,
      specificity: 'b',
      label: `${AntibodyDomainType.VH}b`,
      refs: [6],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.L,
      specificity: '(Heavy 2)',
      label: `${AntibodyDomainType.L}(Heavy 2)`,
      refs: [7],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'b',
      label: `${AntibodyDomainType.VL}b`,
      refs: [8],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'B(Heavy 2)',
      label: 'Mid-chain element 1 (Heavy 2)',
      refs: [16],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: '(Heavy 2)',
      label: `${AntibodyDomainType.H}(Heavy 2)`,
      refs: [12],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: '(Heavy 2)',
      label: `${AntibodyDomainType.CH2}(Heavy 2)`,
      refs: [13],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: '(Heavy 2)',
      label: `${AntibodyDomainType.CH3}(Heavy 2)`,
      refs: [14],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'C(Heavy 2)',
      label: 'C-terminal element (Heavy 2)',
      refs: [22],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'A(Light)',
      label: 'N-terminal element (Light)',
      refs: [23],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'a',
      label: `${AntibodyDomainType.VL}a`,
      refs: [3],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: '(Light)',
      label: `${AntibodyDomainType.CL}(Light)`,
      refs: [4],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'B(Light)',
      label: 'C-terminal element (Light)',
      refs: [24],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy 1',
      refs: [17],
      domainRefsPerChain: [[19, 1, 2, 15, 9, 10, 11, 20]],
      domains: [
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'A(Heavy 1)',
          refs: [19],
          label: 'N-terminal element (Heavy 1)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VH,
          specificity: 'a',
          refs: [1],
          label: `${AntibodyDomainType.VH}a`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: '(Heavy 1)',
          refs: [2],
          label: `${AntibodyDomainType.CH1}(Heavy 1)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'B(Heavy 1)',
          refs: [15],
          label: 'Mid-chain element 1 (Heavy 1)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: '(Heavy 1)',
          refs: [9],
          label: `${AntibodyDomainType.H}(Heavy 1)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: '(Heavy 1)',
          refs: [10],
          label: `${AntibodyDomainType.CH2}(Heavy 1)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: '(Heavy 1)',
          refs: [11],
          label: `${AntibodyDomainType.CH3}(Heavy 1)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'C(Heavy 1)',
          refs: [20],
          label: 'C-terminal element (Heavy 1)',
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy 2',
      refs: [18],
      domainRefsPerChain: [[21, 6, 7, 8, 16, 12, 13, 14, 22]],
      domains: [
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'A(Heavy 2)',
          refs: [21],
          label: 'N-terminal element (Heavy 2)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VH,
          specificity: 'b',
          refs: [6],
          label: `${AntibodyDomainType.VH}b`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.L,
          specificity: '(Heavy 2)',
          refs: [7],
          label: `${AntibodyDomainType.L}(Heavy 2)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VL,
          specificity: 'b',
          refs: [8],
          label: `${AntibodyDomainType.VL}b`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'B(Heavy 2)',
          refs: [16],
          label: 'Mid-chain element 1 (Heavy 2)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: '(Heavy 2)',
          refs: [12],
          label: `${AntibodyDomainType.H}(Heavy 2)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: '(Heavy 2)',
          refs: [13],
          label: `${AntibodyDomainType.CH2}(Heavy 2)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: '(Heavy 2)',
          refs: [14],
          label: `${AntibodyDomainType.CH3}(Heavy 2)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'C(Heavy 2)',
          refs: [22],
          label: 'C-terminal element (Heavy 2)',
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light',
      refs: [5],
      domainRefsPerChain: [[23, 3, 4, 24]],
      domains: [
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'A(Light)',
          refs: [23],
          label: 'N-terminal element (Light)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VL,
          specificity: 'a',
          refs: [3],
          label: `${AntibodyDomainType.VL}a`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: '(Light)',
          refs: [4],
          label: `${AntibodyDomainType.CL}(Light)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'B(Light)',
          refs: [24],
          label: 'C-terminal element (Light)',
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [
    {
      label: 'VHa-VLa pair',
      vhVaVg: {
        type: AntibodyDomainType.VH,
        specificity: 'a',
        label: `${AntibodyDomainType.VH}a`,
        refs: [1],
        __typename: 'DistinctDomainNode',
      },
      vlVbVd: {
        type: AntibodyDomainType.VL,
        specificity: 'a',
        label: `${AntibodyDomainType.VL}a`,
        refs: [3],
        __typename: 'DistinctDomainNode',
      },
      __typename: 'DistinctVariablePairNode',
    },
    {
      label: 'VHb-VLb pair',
      vhVaVg: {
        type: AntibodyDomainType.VH,
        specificity: 'b',
        label: `${AntibodyDomainType.VH}b`,
        refs: [6],
        __typename: 'DistinctDomainNode',
      },
      vlVbVd: {
        type: AntibodyDomainType.VL,
        specificity: 'b',
        label: `${AntibodyDomainType.VL}b`,
        refs: [8],
        __typename: 'DistinctDomainNode',
      },
      __typename: 'DistinctVariablePairNode',
    },
  ],
  interactions: [
    {
      domainRefs: [1, 3],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 4],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [9, 12],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [10, 13],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [11, 14],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 20,
        translate: {
          x: 152.94802540452307,
          y: 290.09102213209053,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 11,
        translate: {
          x: 136.94802540452307,
          y: 236.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 10,
        translate: {
          x: 136.94802540452307,
          y: 168.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 9,
        translate: {
          x: 150.3768749657769,
          y: 149.02684435961459,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        angle: 140.0,
        freeEndLength: 4.0,
        fixedEndLength: 24.0,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 15,
        translate: {
          x: 144.9750592988885,
          y: 136.58921113206867,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 12.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 106.3513351834438,
          y: 84.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 62.64177772475912,
          y: 32.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 19,
        translate: {
          x: 56.78699899541678,
          y: 25.954488933954764,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 22,
        translate: {
          x: 192.94802540452307,
          y: 290.09102213209053,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 14,
        translate: {
          x: 176.94802540452307,
          y: 236.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 13,
        translate: {
          x: 176.94802540452307,
          y: 168.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 12,
        translate: {
          x: 192.94802540452307,
          y: 149.02684435961459,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: true,
        reflectY: false,
        angle: 140.0,
        freeEndLength: 4.0,
        fixedEndLength: 24.0,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 16,
        translate: {
          x: 200.92099151015762,
          y: 136.58921113206867,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 12.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 8,
        translate: {
          x: 207.54471562560235,
          y: 84.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 7,
        translate: {
          x: 216.4656044879819,
          y: 156.9467743258213,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: true,
        reflectY: false,
        centerLength: 64.0,
        endRadius: 11.2,
        tipLength: 4.0,
        __typename: 'LinkerDomainVisualizationNode',
      },
      {
        ref: 6,
        translate: {
          x: 238.18649335036145,
          y: 109.80252651955207,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 21,
        translate: {
          x: 276.0412720797038,
          y: 103.75701545350682,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 23,
        translate: {
          x: 26.14522127065767,
          y: 51.66599332141632,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 32.0,
          y: 57.71150438746156,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 75.70955745868469,
          y: 109.80252651955207,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 24,
        translate: {
          x: 113.56433618802701,
          y: 155.84803758559733,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4.0,
        height: 64.0,
        width: 32.0,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6.0,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 332.09102213209053,
        svgViewboxWidth: 302.1864933503615,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const HETERO_IGG_SCFAB: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  builtIn: false,
  originalVeritas: null,
  archiveData: {
    archived: false,
    purpose: null,
    __typename: 'ArchiveDataNode',
  },
  chainPositions: [
    {
      ref: 25,
      domainRefs: [27, 1, 2, 3, 4, 5, 6, 19, 20, 21, 22, 23, 24, 28],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 26,
      domainRefs: [29, 10, 11, 12, 13, 14, 15, 30],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 9,
      domainRefs: [31, 7, 8, 32],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 18,
      domainRefs: [33, 16, 17, 34],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: 'A(Heavy 1)',
      ref: 27,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 1,
      type: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'A(Heavy 1)',
      ref: 2,
      type: AntibodyDomainType.CH1,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'B(Heavy 1)',
      ref: 3,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 1)',
      ref: 4,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 1)',
      ref: 5,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 1)',
      ref: 6,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'C(Heavy 1)',
      ref: 19,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'c',
      ref: 20,
      type: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'B(Heavy 1)',
      ref: 21,
      type: AntibodyDomainType.CH1,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 1)',
      ref: 22,
      type: AntibodyDomainType.L,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'c',
      ref: 23,
      type: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 1)',
      ref: 24,
      type: AntibodyDomainType.CL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'D(Heavy 1)',
      ref: 28,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'A(Heavy 2)',
      ref: 29,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 10,
      type: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 2)',
      ref: 11,
      type: AntibodyDomainType.CH1,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'B(Heavy 2)',
      ref: 12,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 2)',
      ref: 13,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 2)',
      ref: 14,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy 2)',
      ref: 15,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'C(Heavy 2)',
      ref: 30,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'A(Light 1)',
      ref: 31,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 7,
      type: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Light 1)',
      ref: 8,
      type: AntibodyDomainType.CL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'B(Light 1)',
      ref: 32,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'A(Light 2)',
      ref: 33,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 16,
      type: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Light 2)',
      ref: 17,
      type: AntibodyDomainType.CL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'B(Light 2)',
      ref: 34,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'A(Heavy 1)',
      label: 'N-terminal element (Heavy 1)',
      refs: [27],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VH,
      specificity: 'a',
      label: `${AntibodyDomainType.VH}a`,
      refs: [1],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: 'A(Heavy 1)',
      label: `${AntibodyDomainType.CH1}A(Heavy 1)`,
      refs: [2],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'B(Heavy 1)',
      label: 'Mid-chain element 1 (Heavy 1)',
      refs: [3],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: '(Heavy 1)',
      label: `${AntibodyDomainType.H}(Heavy 1)`,
      refs: [4],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: '(Heavy 1)',
      label: `${AntibodyDomainType.CH2}(Heavy 1)`,
      refs: [5],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: '(Heavy 1)',
      label: `${AntibodyDomainType.CH3}(Heavy 1)`,
      refs: [6],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'C(Heavy 1)',
      label: 'Mid-chain element 2 (Heavy 1)',
      refs: [19],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VH,
      specificity: 'c',
      label: `${AntibodyDomainType.VH}c`,
      refs: [20],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: 'B(Heavy 1)',
      label: `${AntibodyDomainType.CH1}B(Heavy 1)`,
      refs: [21],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.L,
      specificity: '(Heavy 1)',
      label: `${AntibodyDomainType.L}(Heavy 1)`,
      refs: [22],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'c',
      label: `${AntibodyDomainType.VL}c`,
      refs: [23],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: '(Heavy 1)',
      label: `${AntibodyDomainType.CL}(Heavy 1)`,
      refs: [24],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'D(Heavy 1)',
      label: 'C-terminal element (Heavy 1)',
      refs: [28],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'A(Heavy 2)',
      label: 'N-terminal element (Heavy 2)',
      refs: [29],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VH,
      specificity: 'b',
      label: `${AntibodyDomainType.VH}b`,
      refs: [10],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: '(Heavy 2)',
      label: `${AntibodyDomainType.CH1}(Heavy 2)`,
      refs: [11],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'B(Heavy 2)',
      label: 'Mid-chain element 1 (Heavy 2)',
      refs: [12],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: '(Heavy 2)',
      label: `${AntibodyDomainType.H}(Heavy 2)`,
      refs: [13],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: '(Heavy 2)',
      label: `${AntibodyDomainType.CH2}(Heavy 2)`,
      refs: [14],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: '(Heavy 2)',
      label: `${AntibodyDomainType.CH3}(Heavy 2)`,
      refs: [15],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'C(Heavy 2)',
      label: 'C-terminal element (Heavy 2)',
      refs: [30],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'A(Light 1)',
      label: 'N-terminal element (Light 1)',
      refs: [31],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'a',
      label: `${AntibodyDomainType.VL}a`,
      refs: [7],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: '(Light 1)',
      label: `${AntibodyDomainType.CL}(Light 1)`,
      refs: [8],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'B(Light 1)',
      label: 'C-terminal element (Light 1)',
      refs: [32],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'A(Light 2)',
      label: 'N-terminal element (Light 2)',
      refs: [33],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'b',
      label: `${AntibodyDomainType.VL}b`,
      refs: [16],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: '(Light 2)',
      label: `${AntibodyDomainType.CL}(Light 2)`,
      refs: [17],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'B(Light 2)',
      label: 'C-terminal element (Light 2)',
      refs: [34],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy 1',
      refs: [25],
      domainRefsPerChain: [[27, 1, 2, 3, 4, 5, 6, 19, 20, 21, 22, 23, 24, 28]],
      domains: [
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'A(Heavy 1)',
          refs: [27],
          label: 'N-terminal element (Heavy 1)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VH,
          specificity: 'a',
          refs: [1],
          label: `${AntibodyDomainType.VH}a`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: 'A(Heavy 1)',
          refs: [2],
          label: `${AntibodyDomainType.CH1}A(Heavy 1)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'B(Heavy 1)',
          refs: [3],
          label: 'Mid-chain element 1 (Heavy 1)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: '(Heavy 1)',
          refs: [4],
          label: `${AntibodyDomainType.H}(Heavy 1)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: '(Heavy 1)',
          refs: [5],
          label: `${AntibodyDomainType.CH2}(Heavy 1)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: '(Heavy 1)',
          refs: [6],
          label: `${AntibodyDomainType.CH3}(Heavy 1)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'C(Heavy 1)',
          refs: [19],
          label: 'Mid-chain element 2 (Heavy 1)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VH,
          specificity: 'c',
          refs: [20],
          label: `${AntibodyDomainType.VH}c`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: 'B(Heavy 1)',
          refs: [21],
          label: `${AntibodyDomainType.CH1}B(Heavy 1)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.L,
          specificity: '(Heavy 1)',
          refs: [22],
          label: `${AntibodyDomainType.L}(Heavy 1)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VL,
          specificity: 'c',
          refs: [23],
          label: `${AntibodyDomainType.VL}c`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: '(Heavy 1)',
          refs: [24],
          label: `${AntibodyDomainType.CL}(Heavy 1)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'D(Heavy 1)',
          refs: [28],
          label: 'C-terminal element (Heavy 1)',
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy 2',
      refs: [26],
      domainRefsPerChain: [[29, 10, 11, 12, 13, 14, 15, 30]],
      domains: [
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'A(Heavy 2)',
          refs: [29],
          label: 'N-terminal element (Heavy 2)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VH,
          specificity: 'b',
          refs: [10],
          label: `${AntibodyDomainType.VH}b`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: '(Heavy 2)',
          refs: [11],
          label: `${AntibodyDomainType.CH1}(Heavy 2)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'B(Heavy 2)',
          refs: [12],
          label: 'Mid-chain element 1 (Heavy 2)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: '(Heavy 2)',
          refs: [13],
          label: `${AntibodyDomainType.H}(Heavy 2)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: '(Heavy 2)',
          refs: [14],
          label: `${AntibodyDomainType.CH2}(Heavy 2)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: '(Heavy 2)',
          refs: [15],
          label: `${AntibodyDomainType.CH3}(Heavy 2)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'C(Heavy 2)',
          refs: [30],
          label: 'C-terminal element (Heavy 2)',
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light 1',
      refs: [9],
      domainRefsPerChain: [[31, 7, 8, 32]],
      domains: [
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'A(Light 1)',
          refs: [31],
          label: 'N-terminal element (Light 1)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VL,
          specificity: 'a',
          refs: [7],
          label: `${AntibodyDomainType.VL}a`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: '(Light 1)',
          refs: [8],
          label: `${AntibodyDomainType.CL}(Light 1)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'B(Light 1)',
          refs: [32],
          label: 'C-terminal element (Light 1)',
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light 2',
      refs: [18],
      domainRefsPerChain: [[33, 16, 17, 34]],
      domains: [
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'A(Light 2)',
          refs: [33],
          label: 'N-terminal element (Light 2)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VL,
          specificity: 'b',
          refs: [16],
          label: `${AntibodyDomainType.VL}b`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: '(Light 2)',
          refs: [17],
          label: `${AntibodyDomainType.CL}(Light 2)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'B(Light 2)',
          refs: [34],
          label: 'C-terminal element (Light 2)',
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [
    {
      label: 'VHa-VLa pair',
      vhVaVg: {
        type: AntibodyDomainType.VH,
        specificity: 'a',
        label: `${AntibodyDomainType.VH}a`,
        refs: [1],
        __typename: 'DistinctDomainNode',
      },
      vlVbVd: {
        type: AntibodyDomainType.VL,
        specificity: 'a',
        label: `${AntibodyDomainType.VL}a`,
        refs: [7],
        __typename: 'DistinctDomainNode',
      },
      __typename: 'DistinctVariablePairNode',
    },
    {
      label: 'VHb-VLb pair',
      vhVaVg: {
        type: AntibodyDomainType.VH,
        specificity: 'b',
        label: `${AntibodyDomainType.VH}b`,
        refs: [10],
        __typename: 'DistinctDomainNode',
      },
      vlVbVd: {
        type: AntibodyDomainType.VL,
        specificity: 'b',
        label: `${AntibodyDomainType.VL}b`,
        refs: [16],
        __typename: 'DistinctDomainNode',
      },
      __typename: 'DistinctVariablePairNode',
    },
    {
      label: 'VHc-VLc pair',
      vhVaVg: {
        type: AntibodyDomainType.VH,
        specificity: 'c',
        label: `${AntibodyDomainType.VH}c`,
        refs: [20],
        __typename: 'DistinctDomainNode',
      },
      vlVbVd: {
        type: AntibodyDomainType.VL,
        specificity: 'c',
        label: `${AntibodyDomainType.VL}c`,
        refs: [23],
        __typename: 'DistinctDomainNode',
      },
      __typename: 'DistinctVariablePairNode',
    },
  ],
  interactions: [
    {
      domainRefs: [1, 7],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 8],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [10, 16],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [11, 17],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [4, 13],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [5, 14],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [6, 15],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 19,
        translate: {
          x: 145.64968029398344,
          y: 285.09102213209053,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        centerLength: 50.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 6,
        translate: {
          x: 136.94802540452307,
          y: 236.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 5,
        translate: {
          x: 136.94802540452307,
          y: 168.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 150.3768749657769,
          y: 149.02684435961459,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        angle: 140.0,
        freeEndLength: 4.0,
        fixedEndLength: 24.0,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 144.9750592988885,
          y: 136.58921113206867,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 12.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 106.3513351834438,
          y: 84.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 62.64177772475912,
          y: 32.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 27,
        translate: {
          x: 56.78699899541678,
          y: 25.954488933954764,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 20,
        translate: {
          x: 106.3513351834438,
          y: 320.09102213209053,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 21,
        translate: {
          x: 62.64177772475912,
          y: 372.182044264181,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 22,
        translate: {
          x: 62.77566759172191,
          y: 427.28078100440496,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: true,
        reflectY: false,
        centerLength: 132.0,
        endRadius: 11.2,
        tipLength: 4.0,
        __typename: 'LinkerDomainVisualizationNode',
      },
      {
        ref: 23,
        translate: {
          x: 75.70955745868469,
          y: 294.3795177446289,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 24,
        translate: {
          x: 32.0,
          y: 346.47053987671944,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 28,
        translate: {
          x: 26.14522127065767,
          y: 392.51605094276465,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 30,
        translate: {
          x: 192.94802540452307,
          y: 290.09102213209053,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 15,
        translate: {
          x: 176.94802540452307,
          y: 236.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 14,
        translate: {
          x: 176.94802540452307,
          y: 168.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 13,
        translate: {
          x: 192.94802540452307,
          y: 149.02684435961459,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: true,
        reflectY: false,
        angle: 140.0,
        freeEndLength: 4.0,
        fixedEndLength: 24.0,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 12,
        translate: {
          x: 200.92099151015762,
          y: 136.58921113206867,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 12.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 11,
        translate: {
          x: 207.54471562560235,
          y: 84.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 10,
        translate: {
          x: 251.254273084287,
          y: 32.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 29,
        translate: {
          x: 289.1090518136293,
          y: 25.954488933954764,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 31,
        translate: {
          x: 26.14522127065767,
          y: 51.66599332141632,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 7,
        translate: {
          x: 32.0,
          y: 57.71150438746156,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 8,
        translate: {
          x: 75.70955745868469,
          y: 109.80252651955207,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 32,
        translate: {
          x: 113.56433618802701,
          y: 155.84803758559733,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 33,
        translate: {
          x: 319.7508295383884,
          y: 51.66599332141632,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 16,
        translate: {
          x: 281.89605080904613,
          y: 57.71150438746156,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 17,
        translate: {
          x: 238.18649335036145,
          y: 109.80252651955207,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 34,
        translate: {
          x: 232.33171462101913,
          y: 155.84803758559733,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4.0,
        height: 64.0,
        width: 32.0,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6.0,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 523.280781004405,
        svgViewboxWidth: 345.89605080904613,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const SCFV_FC_SCFV: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  builtIn: false,
  originalVeritas: null,
  archiveData: {
    archived: false,
    purpose: null,
    __typename: 'ArchiveDataNode',
  },
  chainPositions: [
    {
      ref: 23,
      domainRefs: [25, 1, 2, 3, 13, 7, 8, 9, 14, 17, 18, 19, 26],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 24,
      domainRefs: [27, 4, 5, 6, 15, 10, 11, 12, 16, 20, 21, 22, 28],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domainPositions: [
    {
      specificity: 'A(Heavy)',
      ref: 25,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 1,
      type: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'A(Heavy)',
      ref: 2,
      type: AntibodyDomainType.L,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 3,
      type: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'B(Heavy)',
      ref: 13,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy)',
      ref: 7,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy)',
      ref: 8,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy)',
      ref: 9,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'C(Heavy)',
      ref: 14,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 17,
      type: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'B(Heavy)',
      ref: 18,
      type: AntibodyDomainType.L,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 19,
      type: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'D(Heavy)',
      ref: 26,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'A(Heavy)',
      ref: 27,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 4,
      type: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'A(Heavy)',
      ref: 5,
      type: AntibodyDomainType.L,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 6,
      type: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'B(Heavy)',
      ref: 15,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy)',
      ref: 10,
      type: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy)',
      ref: 11,
      type: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: '(Heavy)',
      ref: 12,
      type: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'C(Heavy)',
      ref: 16,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 20,
      type: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'B(Heavy)',
      ref: 21,
      type: AntibodyDomainType.L,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 22,
      type: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'D(Heavy)',
      ref: 28,
      type: AntibodyDomainType.ELEMENT,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  domains: [
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'A(Heavy)',
      label: 'N-terminal element (Heavy)',
      refs: [25, 27],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VH,
      specificity: 'a',
      label: `${AntibodyDomainType.VH}a`,
      refs: [1, 4],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.L,
      specificity: 'A(Heavy)',
      label: `${AntibodyDomainType.L}A(Heavy)`,
      refs: [2, 5],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'a',
      label: `${AntibodyDomainType.VL}a`,
      refs: [3, 6],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'B(Heavy)',
      label: 'Mid-chain element 1 (Heavy)',
      refs: [13, 15],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: '(Heavy)',
      label: `${AntibodyDomainType.H}(Heavy)`,
      refs: [7, 10],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: '(Heavy)',
      label: `${AntibodyDomainType.CH2}(Heavy)`,
      refs: [8, 11],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: '(Heavy)',
      label: `${AntibodyDomainType.CH3}(Heavy)`,
      refs: [9, 12],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'C(Heavy)',
      label: 'Mid-chain element 2 (Heavy)',
      refs: [14, 16],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VH,
      specificity: 'b',
      label: `${AntibodyDomainType.VH}b`,
      refs: [17, 20],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.L,
      specificity: 'B(Heavy)',
      label: `${AntibodyDomainType.L}B(Heavy)`,
      refs: [18, 21],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'b',
      label: `${AntibodyDomainType.VL}b`,
      refs: [19, 22],
      optional: false,
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.ELEMENT,
      specificity: 'D(Heavy)',
      label: 'C-terminal element (Heavy)',
      refs: [26, 28],
      optional: true,
      __typename: 'DistinctDomainNode',
    },
  ],
  chains: [
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy',
      refs: [23, 24],
      domainRefsPerChain: [
        [25, 1, 2, 3, 13, 7, 8, 9, 14, 17, 18, 19, 26],
        [27, 4, 5, 6, 15, 10, 11, 12, 16, 20, 21, 22, 28],
      ],
      domains: [
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'A(Heavy)',
          refs: [25, 27],
          label: 'N-terminal element (Heavy)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VH,
          specificity: 'a',
          refs: [1, 4],
          label: `${AntibodyDomainType.VH}a`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.L,
          specificity: 'A(Heavy)',
          refs: [2, 5],
          label: `${AntibodyDomainType.L}A(Heavy)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VL,
          specificity: 'a',
          refs: [3, 6],
          label: `${AntibodyDomainType.VL}a`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'B(Heavy)',
          refs: [13, 15],
          label: 'Mid-chain element 1 (Heavy)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: '(Heavy)',
          refs: [7, 10],
          label: `${AntibodyDomainType.H}(Heavy)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: '(Heavy)',
          refs: [8, 11],
          label: `${AntibodyDomainType.CH2}(Heavy)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: '(Heavy)',
          refs: [9, 12],
          label: `${AntibodyDomainType.CH3}(Heavy)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'C(Heavy)',
          refs: [14, 16],
          label: 'Mid-chain element 2 (Heavy)',
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VH,
          specificity: 'b',
          refs: [17, 20],
          label: `${AntibodyDomainType.VH}b`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.L,
          specificity: 'B(Heavy)',
          refs: [18, 21],
          label: `${AntibodyDomainType.L}B(Heavy)`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VL,
          specificity: 'b',
          refs: [19, 22],
          label: `${AntibodyDomainType.VL}b`,
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.ELEMENT,
          specificity: 'D(Heavy)',
          refs: [26, 28],
          label: 'C-terminal element (Heavy)',
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  distinctVariablePairs: [
    {
      label: 'VHa-VLa pair',
      vhVaVg: {
        type: AntibodyDomainType.VH,
        specificity: 'a',
        label: `${AntibodyDomainType.VH}a`,
        refs: [1, 4],
        __typename: 'DistinctDomainNode',
      },
      vlVbVd: {
        type: AntibodyDomainType.VL,
        specificity: 'a',
        label: `${AntibodyDomainType.VL}a`,
        refs: [3, 6],
        __typename: 'DistinctDomainNode',
      },
      __typename: 'DistinctVariablePairNode',
    },
    {
      label: 'VHb-VLb pair',
      vhVaVg: {
        type: AntibodyDomainType.VH,
        specificity: 'b',
        label: `${AntibodyDomainType.VH}b`,
        refs: [17, 20],
        __typename: 'DistinctDomainNode',
      },
      vlVbVd: {
        type: AntibodyDomainType.VL,
        specificity: 'b',
        label: `${AntibodyDomainType.VL}b`,
        refs: [19, 22],
        __typename: 'DistinctDomainNode',
      },
      __typename: 'DistinctVariablePairNode',
    },
  ],
  interactions: [
    {
      domainRefs: [7, 10],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [8, 11],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [9, 12],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 14,
        translate: {
          x: 101.94012283529875,
          y: 233.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        centerLength: 50.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 9,
        translate: {
          x: 93.23846794583838,
          y: 184.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 8,
        translate: {
          x: 93.23846794583838,
          y: 116.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 7,
        translate: {
          x: 106.66731750709222,
          y: 96.93582222752408,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        angle: 140.0,
        freeEndLength: 4.0,
        fixedEndLength: 24.0,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 13,
        translate: {
          x: 101.26550184020383,
          y: 84.49818899997817,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 12.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 62.64177772475911,
          y: 32.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 40.920888862379556,
          y: 104.8557521937308,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 64.0,
        endRadius: 11.2,
        tipLength: 4.0,
        __typename: 'LinkerDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 31.999999999999993,
          y: 57.711504387461574,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 25,
        translate: {
          x: 26.145221270657657,
          y: 51.66599332141632,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 17,
        translate: {
          x: 62.64177772475911,
          y: 268.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 18,
        translate: {
          x: 40.920888862379556,
          y: 315.1442478062692,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: true,
        reflectY: false,
        centerLength: 64.0,
        endRadius: 11.2,
        tipLength: 4.0,
        __typename: 'LinkerDomainVisualizationNode',
      },
      {
        ref: 19,
        translate: {
          x: 31.999999999999993,
          y: 242.28849561253844,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 26,
        translate: {
          x: 26.145221270657657,
          y: 288.3340066785837,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 16,
        translate: {
          x: 156.536813056378,
          y: 233.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        centerLength: 50.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 12,
        translate: {
          x: 133.23846794583838,
          y: 184.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 11,
        translate: {
          x: 133.23846794583838,
          y: 116.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 10,
        translate: {
          x: 149.23846794583838,
          y: 96.93582222752408,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0.0,
        reflectX: true,
        reflectY: false,
        angle: 140.0,
        freeEndLength: 4.0,
        fixedEndLength: 24.0,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 15,
        translate: {
          x: 157.21143405147293,
          y: 84.49818899997817,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 12.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 6,
        translate: {
          x: 163.83515816691767,
          y: 32.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 5,
        translate: {
          x: 172.7560470292972,
          y: 104.8557521937308,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: true,
        reflectY: false,
        centerLength: 64.0,
        endRadius: 11.2,
        tipLength: 4.0,
        __typename: 'LinkerDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 194.47693589167676,
          y: 57.711504387461574,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 27,
        translate: {
          x: 232.3317146210191,
          y: 51.66599332141632,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 20,
        translate: {
          x: 163.83515816691767,
          y: 268.0,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 21,
        translate: {
          x: 172.7560470292972,
          y: 315.1442478062692,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 64.0,
        endRadius: 11.2,
        tipLength: 4.0,
        __typename: 'LinkerDomainVisualizationNode',
      },
      {
        ref: 22,
        translate: {
          x: 194.47693589167676,
          y: 242.28849561253844,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 28,
        translate: {
          x: 232.3317146210191,
          y: 288.3340066785837,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40.0,
        reflectX: false,
        reflectY: false,
        centerLength: 24.0,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4.0,
        height: 64.0,
        width: 32.0,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6.0,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 411.1442478062692,
        svgViewboxWidth: 258.4769358916768,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};
